import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";

import { AuthBrandHeader } from "@/components/portal/AuthBrandHeader";
import { HomeTopNav } from "@/components/portal/HomeTopNav";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useVerificationResendCooldown } from "@/hooks/useVerificationResendCooldown";
import { getAuthSession } from "@/lib/auth/session";
import { resendVerification } from "@/lib/api/resend-verification";
import { formatVerificationResendCooldownLabel } from "@/lib/api/verification-resend-cooldown";
import { verifyEmail } from "@/lib/api/verify-email";
import {
  getResendVerificationErrorMessage,
  RESEND_VERIFICATION_SUCCESS_MESSAGE,
  resolveVerifyEmailResult,
  shouldOfferResendVerification,
  VERIFY_EMAIL_SUCCESS_MESSAGE,
  type VerifyEmailStatus,
} from "@/lib/api/verify-email-form";
import { PORTAL_HOME_ROUTE } from "@/lib/portal/portal-routes";

export default function VerifyEmailPage() {
  usePageTitle("Verify email");
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() ?? "";
  const emailFromQuery = searchParams.get("email")?.trim() ?? "";

  const [status, setStatus] = useState<VerifyEmailStatus>(
    token ? "loading" : "invalid",
  );
  const [message, setMessage] = useState(
    token ? "Verifying your email…" : "This verification link is missing a token.",
  );
  const [resendEmail, setResendEmail] = useState(emailFromQuery);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendNotice, setResendNotice] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const resendCooldown = useVerificationResendCooldown();

  useEffect(() => {
    if (!token) {
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const response = await verifyEmail({ token });
        if (cancelled) {
          return;
        }
        setStatus("success");
        setMessage(response.message || VERIFY_EMAIL_SUCCESS_MESSAGE);
      } catch (err: unknown) {
        if (cancelled) {
          return;
        }
        const result = resolveVerifyEmailResult(err);
        setStatus(result.status);
        setMessage(result.message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  if (getAuthSession()) {
    return <Navigate to={PORTAL_HOME_ROUTE} replace />;
  }

  async function handleResend(event?: { preventDefault(): void }) {
    event?.preventDefault();
    const email = resendEmail.trim();
    if (!email) {
      setResendError("Enter the email address you used to register.");
      return;
    }
    if (resendCooldown.isCoolingDown) {
      return;
    }

    setResendLoading(true);
    setResendError(null);
    setResendNotice(null);

    try {
      const response = await resendVerification({ email });
      setResendNotice(response.message || RESEND_VERIFICATION_SUCCESS_MESSAGE);
      resendCooldown.start();
    } catch (err: unknown) {
      setResendError(getResendVerificationErrorMessage(err));
    } finally {
      setResendLoading(false);
    }
  }

  const showResend = shouldOfferResendVerification(status);

  return (
    <>
      <HomeTopNav />
      <main id="main-content" className="auth-shell">
        <div className="auth-layout">
          <AuthBrandHeader testId="home-verify-email-brand-logo" />

          <div className="auth-card" data-testid="home-verify-email-card">
            <div className="auth-hdr">
              <p className="auth-eye">Email verification</p>
              <h1 data-testid="home-verify-email-title">Verify your email</h1>
              <p>Confirm your address to activate your developer account.</p>
            </div>
            <div className="auth-body">
              {status === "loading" ? (
                <div
                  className="notice-msg"
                  data-testid="home-verify-email-loading"
                  role="status"
                >
                  <i
                    className="ti ti-loader-2"
                    style={{ animation: "spin 1s linear infinite" }}
                  />
                  <span>{message}</span>
                </div>
              ) : null}

              {status === "success" || status === "already_verified" ? (
                <div
                  className="success-msg"
                  data-testid="home-verify-email-success"
                  role="status"
                >
                  <i className="ti ti-circle-check" />
                  <span>{message}</span>
                </div>
              ) : null}

              {status === "invalid" ||
              status === "expired" ||
              status === "error" ? (
                <div
                  className="error-msg"
                  data-testid="home-verify-email-error"
                  role="alert"
                >
                  <i className="ti ti-alert-circle" />
                  <span>{message}</span>
                </div>
              ) : null}

              {status === "success" || status === "already_verified" ? (
                <Link
                  to={PORTAL_HOME_ROUTE}
                  className="submit-btn auth-submit"
                  data-testid="home-verify-email-login-link"
                  style={{ textDecoration: "none" }}
                >
                  <i className="ti ti-login" /> Continue to log in
                </Link>
              ) : null}

              {showResend ? (
                <form
                  className="auth-form"
                  data-testid="home-verify-email-resend-form"
                  noValidate
                  onSubmit={(event) => {
                    void handleResend(event);
                  }}
                >
                  <p style={{ margin: 0, fontSize: 12, color: "var(--t2)" }}>
                    Need a new link? Enter your email and we&apos;ll send
                    another verification message.
                  </p>
                  {resendNotice ? (
                    <div
                      className="success-msg"
                      data-testid="home-verify-email-resend-success"
                      role="status"
                    >
                      <i className="ti ti-circle-check" />
                      <span>{resendNotice}</span>
                    </div>
                  ) : null}
                  {resendError ? (
                    <div
                      className="error-msg"
                      data-testid="home-verify-email-resend-error"
                      role="alert"
                    >
                      <i className="ti ti-alert-circle" />
                      <span>{resendError}</span>
                    </div>
                  ) : null}
                  <div className="field">
                    <label htmlFor="home-verify-email-resend-email">Email</label>
                    <input
                      id="home-verify-email-resend-email"
                      type="email"
                      data-testid="home-verify-email-resend-email"
                      placeholder="you@company.com"
                      value={resendEmail}
                      onChange={(e) => {
                        setResendEmail(e.target.value);
                        setResendError(null);
                      }}
                    />
                  </div>
                  <button
                    type="submit"
                    className="submit-btn auth-submit"
                    data-testid="home-verify-email-resend-submit"
                    disabled={resendLoading || resendCooldown.isCoolingDown}
                  >
                    {resendLoading ? (
                      <>
                        <i
                          className="ti ti-loader-2"
                          style={{ animation: "spin 1s linear infinite" }}
                        />{" "}
                        Sending…
                      </>
                    ) : (
                      <>
                        <i className="ti ti-mail" />{" "}
                        {formatVerificationResendCooldownLabel(
                          resendCooldown.secondsRemaining,
                        )}
                      </>
                    )}
                  </button>
                </form>
              ) : null}
            </div>
          </div>

          <p className="already">
            Already verified?{" "}
            <Link to={PORTAL_HOME_ROUTE} data-testid="home-verify-email-footer-login">
              Log in →
            </Link>
          </p>
        </div>
      </main>
    </>
  );
}
